import React, { useState } from "react";
import { Form, Input, InputNumber, Button, DatePicker, Space } from "antd";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../shared/utils/Util";
import { fetchT } from "../../shared/utils/TypedUtil";
import { useNotification } from "../../shared/hooks";
import { CreatePointGiftCardRequest } from "./types";

type PointGiftCardFormProps = {
  restaurantId: string;
  onSuccess: () => void;
};

export const PointGiftCardForm: React.FC<PointGiftCardFormProps> = ({
  restaurantId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { createSuccess, notifyError } = useNotification();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = (values: { description: string; amount: number; expire_time?: any }) => {
    setLoading(true);

    const request: CreatePointGiftCardRequest = {
      description: values.description,
      amount: values.amount,
      expire_time: values.expire_time ? values.expire_time.toISOString() : undefined,
    };

    fetchT<{ id: string; point_credit_id: string }>(
      "POST",
      `${API_URL}/v1/r/${restaurantId}/admin/point_gift_cards`,
      request,
      () => {
        createSuccess(t("point_gift_card"));
        form.resetFields();
        onSuccess();
        setLoading(false);
      },
      (err) => {
        notifyError(t("failed_to_create_gift_card"), err);
        setLoading(false);
      }
    );
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Form.Item
        name="description"
        label={t("description")}
        rules={[{ required: true, message: t("description_is_required") }]}
      >
        <Input placeholder={t("enter_description")} />
      </Form.Item>

      <Form.Item
        name="amount"
        label={t("amount")}
        rules={[
          { required: true, message: t("amount_is_required") },
          {
            validator(_, value) {
              if (value !== undefined && value !== null && value <= 0) {
                return Promise.reject(new Error(t("amount_must_be_greater_than_zero")));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <InputNumber
          style={{ width: "100%" }}
          min={1}
          step={1}
          precision={0}
          placeholder={t("enter_amount")}
        />
      </Form.Item>

      <Form.Item name="expire_time" label={t("expiry_date")} tooltip={t("expiry_date_tooltip")}>
        <DatePicker style={{ width: "100%" }} placeholder={t("select_expiry_date")} />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {t("create_gift_card")}
          </Button>
          <Button onClick={() => form.resetFields()}>{t("reset")}</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
